import { useMemo, useCallback } from "react";
import { DateTime } from "luxon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  useFetchPostLikesQuery,
  useFetchUserProfilesByIdsQuery,
  type UserProfile,
} from "@/api/clubsApi";
import { userDisplayName } from "@tinyclub/shared/utils";
import { useProfileSidePanel } from "@/components/profile";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";

interface PostLikesDialogProps {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PostLikesDialog({ postId, open, onOpenChange }: PostLikesDialogProps) {
  const { openProfile } = useProfileSidePanel();

  const handleUserClick = useCallback(
    (userId: string) => {
      onOpenChange(false); // Close the dialog
      openProfile(userId); // Open profile side panel
    },
    [onOpenChange, openProfile]
  );

  const { data: likes, isLoading: likesLoading } = useFetchPostLikesQuery(
    { postId },
    { skip: !open }
  );

  const userIds = useMemo(
    () => (likes || []).map((l) => l.user_id).filter(Boolean),
    [likes]
  );

  const { data: profiles, isLoading: profilesLoading } = useFetchUserProfilesByIdsQuery(
    userIds,
    { skip: userIds.length === 0 || !open }
  );

  const profileMap = useMemo(() => {
    const map = new Map<string, UserProfile>();
    profiles?.forEach((p) => map.set(p.id, p));
    return map;
  }, [profiles]);

  const isLoading = likesLoading || profilesLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm max-h-[60vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Likes</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6">
          {isLoading && <LikesSkeleton />}

          {!isLoading && (!likes || likes.length === 0) && (
            <p className="text-body-2 text-text-secondary text-center py-8">
              No likes yet.
            </p>
          )}

          {!isLoading && likes && likes.length > 0 && (
            <div className="space-y-1">
              {likes.map((like) => {
                const profile = profileMap.get(like.user_id);
                return (
                  <LikeRow
                    key={like.id}
                    profile={profile}
                    likedAt={like.created_at}
                    onUserClick={handleUserClick}
                  />
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LikeRow({
  profile,
  likedAt,
  onUserClick,
}: {
  profile?: UserProfile;
  likedAt: string;
  onUserClick: (userId: string) => void;
}) {
  const displayName = profile ? userDisplayName(profile) : "Unknown";
  const avatar = profile?.avatar_small || profile?.avatar_medium;
  const initials = displayName
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const relativeTime = formatRelativeTime(likedAt);

  return (
    <button
      onClick={() => profile?.id && onUserClick(profile.id)}
      className="w-full flex items-center gap-3 py-2 hover:bg-surface rounded-lg px-2 -mx-2 transition-colors text-left"
    >
      {/* Avatar with heart badge */}
      <div className="relative flex-shrink-0">
        <div
          className="rounded-full overflow-hidden bg-surface-2"
          style={{ width: 40, height: 40 }}
        >
          {avatar ? (
            <img src={avatar} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs font-medium text-text-secondary">
              {initials}
            </div>
          )}
        </div>
        {/* Heart badge overlay — matches mobile */}
        <div
          className="absolute flex items-center justify-center bg-red rounded-full"
          style={{ width: 20, height: 20, bottom: -2, right: -2 }}
        >
          <Icon name="heart" size={10} className="text-white" />
        </div>
      </div>

      {/* Name and time */}
      <div className="flex-1 min-w-0">
        <p className="text-body-2-medium text-text-primary truncate">{displayName}</p>
        <p className="text-body-3 text-text-tertiary">{relativeTime}</p>
      </div>
    </button>
  );
}

function LikesSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 py-2">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-4 w-32 mb-1" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = DateTime.fromISO(dateString);
  const now = DateTime.now();
  const diff = now.diff(date, ["days", "hours", "minutes"]);

  if (diff.days >= 7) return date.toFormat("MMM d");
  if (diff.days >= 1) return `${Math.floor(diff.days)}d ago`;
  if (diff.hours >= 1) return `${Math.floor(diff.hours)}h ago`;
  if (diff.minutes >= 1) return `${Math.floor(diff.minutes)}m ago`;
  return "just now";
}
